const MAX_BYTES = 200 * 1024 * 1024;
const MAX_MS = 12 * 60_000;
const EXTENSION = /\.(wav|flac|mp3|m4a)$/i;

export const AUDIO_ACCEPT = 'audio/*,.wav,.flac,.mp3,.m4a';

export type AudioFileCheck =
  | { ok: true; durationMs: number | undefined }
  | { ok: false; message: string };

/** Reads the duration the browser can decode, or undefined when it cannot
 * (Safari and FLAC, for one). The server never trusts this; it only shapes the
 * file card and the twelve-minute check. */
function probeDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value: number | undefined) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => done(undefined), 5000);
    audio.addEventListener('loadedmetadata', () => {
      clearTimeout(timer);
      done(Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : undefined);
    });
    audio.addEventListener('error', () => {
      clearTimeout(timer);
      done(undefined);
    });
    audio.preload = 'metadata';
    audio.src = url;
  });
}

/** Client-side checks for the create flow's drop zone: one of the four
 * extensions, 200 MB, twelve minutes. R2 CORS and the server remain the real
 * enforcers. */
export async function inspectAudioFile(file: File): Promise<AudioFileCheck> {
  if (!EXTENSION.test(file.name)) {
    return { ok: false, message: 'Choose a WAV, FLAC, MP3 or M4A file.' };
  }
  if (file.size > MAX_BYTES) return { ok: false, message: 'That file is over 200 MB.' };
  const durationMs = await probeDuration(file);
  if (durationMs !== undefined && durationMs > MAX_MS) {
    return { ok: false, message: 'That recording is longer than twelve minutes.' };
  }
  return { ok: true, durationMs };
}

/** A starting title and artist from a file name: "Artist - Title.wav" splits,
 * anything else becomes the title with hyphens and underscores turned into
 * spaces. */
export function draftFromFileName(name: string): { title: string; artist: string } {
  const stem = name.replace(EXTENSION, '').replace(/\.[^.]+$/, '');
  const dash = stem.split(/\s+-\s+/);
  const clean = (part: string) => part.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (dash.length === 2 && dash[0] && dash[1]) return { artist: clean(dash[0]), title: clean(dash[1]) };
  return { title: clean(stem), artist: '' };
}

export function humanSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
